
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotification } from '@/context/NotificationContext';
import { NotificationPosition } from '@/types/notifications';
import { cn } from '@/lib/utils';

// Lazy load notification item for better performance
const NotificationItem = lazy(() => import('./NotificationItem'));

// Optimized notification skeleton
const NotificationSkeleton = () => (
  <div className="pointer-events-auto">
    <div className="bg-card border rounded-lg shadow-lg p-4 space-y-2 animate-pulse">
      <div className="h-4 bg-popover rounded w-3/4"></div>
      <div className="h-3 bg-popover rounded w-full"></div>
    </div>
  </div>
);

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotification();
  const [navbarHeight, setNavbarHeight] = useState(72); // Default fallback

  // Calculate actual navbar height dynamically
  useEffect(() => {
    const calculateNavbarHeight = () => {
      const navbar = document.querySelector('nav');
      if (navbar) {
        const height = navbar.offsetHeight;
        setNavbarHeight(height);
      }
    };

    // Calculate on mount
    calculateNavbarHeight();

    // Recalculate on resize to handle responsive changes
    const handleResize = () => {
      calculateNavbarHeight();
    };

    window.addEventListener('resize', handleResize);
    
    // Also listen for DOM changes in case navbar content changes
    const observer = new MutationObserver(calculateNavbarHeight);
    const navbar = document.querySelector('nav');
    if (navbar) {
      observer.observe(navbar, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  // Group notifications by position for better performance
  const notificationsByPosition = notifications.reduce((acc, notification) => {
    const position = notification.position || 'top-right';
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(notification);
    return acc;
  }, {} as Record<NotificationPosition, typeof notifications>);

  const getPositionClasses = (position: NotificationPosition): string => {
    // Add 8px buffer below navbar
    const topOffset = navbarHeight + 8;
    
    switch (position) {
      case 'top-right':
        return `right-4 items-end`;
      case 'top-center':
        return `left-1/2 transform -translate-x-1/2 items-center`;
      case 'top-left':
        return `left-4 items-start`;
      case 'bottom-right':
        return `bottom-4 right-4 items-end`;
      case 'bottom-center':
        return `bottom-4 left-1/2 transform -translate-x-1/2 items-center`;
      case 'bottom-left':
        return `bottom-4 left-4 items-start`;
      default:
        return `right-4 items-end`;
    }
  };

  const getTopPosition = (position: NotificationPosition): React.CSSProperties => {
    if (position.startsWith('top-')) {
      return { top: `${navbarHeight + 8}px` };
    }
    return {};
  };

  const containerContent = Object.entries(notificationsByPosition).map(([position, positionNotifications]) => (
    <div
      key={position}
      className={cn(
        'fixed z-[55] flex flex-col space-y-3 pointer-events-none',
        getPositionClasses(position as NotificationPosition)
      )}
      style={getTopPosition(position as NotificationPosition)}
    >
      {positionNotifications.map((notification) => (
        <Suspense key={notification.id} fallback={<NotificationSkeleton />}>
          <NotificationItem
            notification={notification}
            onRemove={removeNotification}
          />
        </Suspense>
      ))}
    </div>
  ));

  return typeof document !== 'undefined' 
    ? createPortal(containerContent, document.body)
    : null;
};

export default NotificationContainer;
